import { API_POST, DBRequest } from '../../api/api';
import * as Sentry from '@sentry/browser'; // or '@sentry/react'

class PushError extends Error {
  constructor(message, details) {
    super(message);
    this.details = details;
  }
}

/*
//Function which allows to test timeouts (!DO NOT USE IN PRODUCTION)
function forever() {
  return new Promise(() => {});
}
// Usage await forever();
//*/

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function withTimeout(promise, ms, error = new Error('Timeout')) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(error), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      }
    );
  });
}

export async function subscribeForPush(config, publicVapidKey, alreadyGranted) {
  // 1. Ask for permission
  if (!alreadyGranted) {
    if (!('Notification' in window)) {
      return 'denied';
    }
    let permission;
    try {
      permission = await withTimeout(
        Notification?.requestPermission(),
        10000,
        new PushError('Permission check timeout', {
          Notification,
          publicVapidKey,
          alreadyGranted,
          permission,
        })
      );
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      return 'error';
    }

    if (permission !== 'granted') return 'denied';
  }

  /*const registration = await navigator.serviceWorker.ready;

  //Unsubscribe from existing subscriptions
  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    await existingSubscription.unsubscribe();
  }*/

  if (!publicVapidKey) return 'error';

  // 2. Register service worker
  try {
    await withTimeout(
      navigator.serviceWorker.register('/service-worker.js'),
      10000, // 10 seconds
      new PushError('Service worker registration timeout', {
        publicVapidKey,
        alreadyGranted,
        navigator,
      })
    );
  } catch (err) {
    console.error(err);
    Sentry.captureException(err);
    return 'error';
  }

  //wait for the worker to be ready
  let registration;
  try {
    registration = await withTimeout(
      navigator.serviceWorker.ready,
      10000, // 10 seconds
      new PushError('Service worker not ready in time', {
        publicVapidKey,
        alreadyGranted,
        navigator,
        registration,
      })
    );
  } catch (err) {
    console.error(err);
    Sentry.captureException(err);
    return 'error';
  }

  // 3. Subscribe using browser PushManager
  let subscription;
  try {
    subscription = await withTimeout(
      registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicVapidKey),
      }),
      10000,
      new PushError('Push subscription timeout', {
        publicVapidKey,
        alreadyGranted,
        navigator,
        registration,
        base64VapidKey: urlBase64ToUint8Array(publicVapidKey),
        subscription,
      })
    );
  } catch (err) {
    console.error(err);
    Sentry.captureException(err);
    return 'error';
  }

  const supportedEncodings = PushManager.supportedContentEncodings;
  const usedEconding = supportedEncodings[supportedEncodings.length - 1];

  return new Promise((resolve, reject) => {
    DBRequest({
      config,
      path: 'web-push/subscribe',
      method: API_POST({
        ...subscription.toJSON(),
        contentEncoding: usedEconding,
      }),
      onResponse: (res) => {
        resolve('success');
      },
      onError: (error) => {
        reject('error');
      },
    });
  });
}

export async function unsubscribeFromPush(config) {
  // 1. Get the service worker registration
  const registration = await navigator?.serviceWorker?.ready;

  // 2. Get the existing subscription
  const subscription = await registration?.pushManager?.getSubscription();

  if (!subscription) {
    return 'no-subscription';
  }

  // 3. Unsubscribe in the browser
  await subscription?.unsubscribe();

  // 4. Notify backend to remove it
  return new Promise((resolve, reject) => {
    DBRequest({
      config,
      path: 'web-push/unsubscribe',
      method: API_POST(subscription), // send subscription info to delete on backend
      onResponse: (res) => {
        resolve('success');
      },
      onError: (error) => {
        reject('error');
      },
    });
  });
}
